import React from 'react';
import { styled } from '@glitz/react';
import { ToggleableField } from 'Shared/Accordion';
import CheckoutUpdateResultType from '../Pages/Checkout/CheckoutUpdateResult.type';
import GiftCardViewModel from './GiftCardPaymentViewModel.type';
import Price from 'Pricing/Price';
import { Close16x16 } from 'Shared/Icons/Close';
import FeedbackWrapper from 'Shared/FeedbackWrapper';
import Input from 'Shared/Fields/Text';
import FeedbackButton, { ButtonTheme } from 'Shared/Button/Feedback';

const Remove = styled(FeedbackWrapper, {
});

const Content = styled.div({
});

const CodeItem = styled.span({
  fontWeight: 'bold',
  flexGrow: 1,
});

const Item = styled.div({
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
});

type PropType = {
  header: string;
  buttonText: string;
  placeholderText: string;
  giftCards: GiftCardViewModel[];
  isMobile: boolean;
  cartTotal: number;
  addGiftCardCode: (cardNumber: string) => Promise<CheckoutUpdateResultType>;
  removeGiftCardCode: (value: string) => Promise<void>;
  giftCardValidation: string;
  totalPrice: number;
  labels: {
    genericError: string,
  }
};

type StateType = {
  isOpen?: boolean;
  errorMessage?: string;
  value: string;
};

export default class GiftCard extends React.Component<PropType, StateType> {
  defaultRegex = '[\\s\\S]+';
  constructor(props: PropType) {
    super(props);
    this.state = {
      isOpen: props.giftCards?.length > 0,
      errorMessage: '',
      value: '',
    };
  }

  async removeGiftCardCode(code: string) {
    const action = async () => {
      await this.props.removeGiftCardCode(code);
      return Promise.resolve(null);
    };

    this.setState({ errorMessage: null });

    try {
      const promise = action();
      await promise;
    } catch (e) {
      console.log(e);
    }
  }

  handleChange(e: HTMLInputElement, index: number) {
    const { value } = e;
    const giftCards = this.props.giftCards;
    if (giftCards[index].availableAmount >= Number(value) && this.props.cartTotal >= Number(value)) {
      giftCards[index].amount = Number(value);
    }
  }

  async addGiftCardCode() {
    const code = this.state.value;
    try {
      const { success, message } = await this.props.addGiftCardCode(code);
      if (success) {
        this.setState({ value: '' });
        return Promise.resolve();
      } else {
        this.setState({ errorMessage: message });
        return Promise.reject(null);
      }
    } catch (e) {
      this.setState({ errorMessage: this.props.labels.genericError });
      return Promise.reject(null);
    }
  }

  onChange(inValue: string) {
    // trim whitespace
    const value = (inValue || '').replace(/\s+/g, '');
    let regex: RegExp;
    try {
      regex = new RegExp(this.props.giftCardValidation);
    } catch (e) {
      regex = new RegExp(this.defaultRegex);
    }
    const match = regex.test(value);
    this.setState({
      value,
      errorMessage: match || value?.length === 0 ? null : this.props.labels.genericError,
    });
  }

  onToggle = () => this.setState({ isOpen: !this.state.isOpen });
  render() {
    const { giftCards, isMobile, giftCardValidation } = this.props;
    const { value, errorMessage } = this.state;
    const giftCardList = giftCards.map((giftCard, i) => {
      const usedAmount = 0 - giftCard.amount;
      const data = (
        <Item key={i}>
          <Remove flexInnerDiv css={{ marginRight: '6px' }} feedback={() => this.removeGiftCardCode(giftCard.cardNumber)}>
            <Close16x16 />
          </Remove>
          <CodeItem>
            {giftCard.cardNumber}
          </CodeItem>
          <Price
            sellingPrice={usedAmount}
            listPrice={usedAmount}
            fontSize={16}
          />
        </Item>
      );
      return data;
    });
    const Header =
      this.props.giftCards?.length > 0 ? (
        this.props.header + " (" + this.props.giftCards.length + ")"
      ) : (
        this.props.header
      );

    return (
      <ToggleableField iconWidth='16px' upperCaseHeading headerFontSize='14px' noBorder extraPadding header={Header} isOpen={this.state.isOpen} onToggle={this.onToggle}>
        <Content>
          {giftCardList &&
            <styled.Div
              css={{
                display: 'flex',
                flexDirection: 'column',
                ...(this.state.isOpen && {
                  marginTop: '8px'
                })
              }}
            >
              {giftCardList}
            </styled.Div>
          }
          {this.props.totalPrice !== 0 &&
            <styled.Div
              css={{
                display: 'flex',
                ...(this.state.isOpen && {
                  marginTop: '14px'
                })
              }}
            >
              <Input
                css={{
                  flexGrow: 1,
                  paddingRight: '12px'
                }}
                value={value}
                invalid={!!errorMessage || this.props.totalPrice === 0}
                errorText={errorMessage}
                type="tel"
                pattern={giftCardValidation ? giftCardValidation : this.defaultRegex}
                onChange={e => this.onChange(e.currentTarget.value)}
                placeholder={this.props.placeholderText}
              />
              <FeedbackButton
                onClick={this.addGiftCardCode.bind(this)}
                theme={ButtonTheme.BlackToWhite}
                disabled={!value || !!errorMessage}
              >
                {this.props.buttonText}
              </FeedbackButton>
            </styled.Div>
          }
        </Content>
      </ToggleableField>
    );
  }
}
